<template>
  <!--
    A card in the thread (CONCIERGE_CHAT_FIRST.md §5.2), in the visual language of the owner's design (the «Условия
    платежа» card, docs/design-2026-10-24). The block is a pointer: what it says comes from the card's state in the poll
    (`state`, which carries the concierge cards' data); the concierge cards make their own calls (cards/*.vue).
  -->
  <!-- The concierge's first line of a task greets the client, by name when the profile has one. -->
  <p v-if="block.type === 'CONCIERGE_EVENT'" class="cb-event" data-testid="chat-block-event" :data-event="block.event">{{
    eventLine(block) }}</p>

  <!-- A top-up's status line; COMPLETED is the closing card with the credited amount (02 §5.1 В-3). -->
  <div v-else-if="block.type === 'TOPUP_EVENT' && block.event === 'COMPLETED'" class="cb" data-testid="chat-block-topup-done">
    <div class="cb__title">{{ t('cabinet.topupChat.doneTitle') }}</div>
    <div v-if="credited" class="cb__row cb__summary" data-testid="topup-done-amount">
      <span>{{ t('cabinet.topupChat.credited') }}</span>
      <strong class="num">+{{ f.money(credited.amount, credited.currency) }} {{ credited.currency }}</strong>
    </div>
    <p class="cb__hint">{{ t('cabinet.topupChat.doneBody') }}</p>
  </div>
  <p v-else-if="block.type === 'TOPUP_EVENT'" class="cb-event cb-event--line" data-testid="chat-block-topup-event" :data-event="block.event">
    {{ t(topUpEventKey(block.event, topUpState?.request?.sourceKind)) }}
  </p>
  <CardActionCard v-else-if="block.type === 'CARD_ACTION'" :stage="block.stage"
                  :state="state?.type === 'CARD_ACTION' ? state : undefined" />
  <TopUpRequestCard v-else-if="block.type === 'TOPUP_REQUEST'"
                    :state="state?.type === 'TOPUP_REQUEST' ? state : undefined" :busy="busy" :reread="reread" />
  <TopUpPayInCard v-else-if="block.type === 'TOPUP_PAY_IN'"
                  :state="state?.type === 'TOPUP_PAY_IN' ? state : undefined" :busy="busy" :reread="reread" />

  <TermsCard v-else-if="block.type === 'CONCIERGE_TERMS'" :state="state?.type === 'CONCIERGE_TERMS' ? state : undefined"
             :request-id="task?.id ?? null" :task-closed="taskClosed" :busy="busy" :reread="reread" />
  <PayInCard v-else-if="block.type === 'CONCIERGE_PAY_IN'" :state="state?.type === 'CONCIERGE_PAY_IN' ? state : undefined"
             :request-id="task?.id ?? null" :task-status="task?.status ?? null" :busy="busy" :reread="reread" />
  <RefundToCard v-else-if="block.type === 'CONCIERGE_REFUND_TO'"
                :state="state?.type === 'CONCIERGE_REFUND_TO' ? state : undefined"
                :request-id="task?.id ?? null" :busy="busy" :reread="reread" />
  <MoneyRequestCard v-else-if="block.type === 'MONEY_REQUEST'"
                    :state="state?.type === 'MONEY_REQUEST' ? state : undefined" :busy="busy" :reread="reread" />
  <IdentityVouchCard v-else-if="block.type === 'IDENTITY_VOUCH'"
                     :state="state?.type === 'IDENTITY_VOUCH' ? state : undefined" :busy="busy" :reread="reread" />

  <div v-else class="cb" :data-testid="`chat-block-${block.type}`">
    <!-- The manager asks for a document: «Прикрепить» opens this thread's file picker. -->
    <template v-if="block.type === 'REQUEST_DOCUMENT'">
      <div class="cb__title">{{ t('vitrine.concierge.document.title') }}</div>
      <p v-if="block.note" class="cb__text">{{ block.note }}</p>
      <button v-if="open" type="button" class="cb__secondary" :disabled="disabled" data-testid="chat-block-attach"
              @click="emit('attach')">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m8 13 6-6a3 3 0 0 1 4 4l-8 8a5 5 0 0 1-7-7L13 2" /></svg>
        {{ t('vitrine.concierge.document.attach') }}
      </button>
      <div v-else class="cb__state" data-testid="chat-block-state">{{ t('vitrine.concierge.document.received') }}</div>
    </template>

    <!-- A question with its options: a tap sends the option's text as the client's answer. -->
    <template v-else-if="block.type === 'QUESTION'">
      <p class="cb__text cb__text--lead">{{ block.text }}</p>
      <div v-if="open" class="cb__options">
        <button v-for="o in block.options" :key="o" type="button" class="cb__secondary" :disabled="disabled"
                data-testid="chat-block-option" @click="emit('answer', o)">{{ o }}</button>
      </div>
      <div v-else class="cb__state" data-testid="chat-block-state">{{ t('vitrine.concierge.block.answered') }}</div>
    </template>

    <!-- One field the manager asks to fill: the value goes back as the client's answer. -->
    <template v-else-if="block.type === 'REQUEST_INPUT'">
      <div class="cb__title">{{ t('vitrine.concierge.input.title') }}</div>
      <p class="cb__text">{{ block.label }}</p>
      <form v-if="open" class="cb__form" @submit.prevent="sendInput">
        <input v-model="value" class="cb__input" maxlength="4000" :aria-label="block.label" :disabled="disabled"
               data-testid="chat-block-input">
        <button type="submit" class="cb__primary" :disabled="disabled || !value.trim()" data-testid="chat-block-send">
          {{ t('vitrine.concierge.input.send') }}
        </button>
      </form>
      <div v-else class="cb__state" data-testid="chat-block-state">{{ t('vitrine.concierge.block.answered') }}</div>
    </template>

    <p v-if="error" class="cb__err" data-testid="chat-block-error">{{ error }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { BlockState, ChatBlock } from '@/api/support';
import type { ConciergeRequestView } from '@/api/concierge';
import IdentityVouchCard from './cards/IdentityVouchCard.vue';
import MoneyRequestCard from './cards/MoneyRequestCard.vue';
import PayInCard from './cards/PayInCard.vue';
import RefundToCard from './cards/RefundToCard.vue';
import TermsCard from './cards/TermsCard.vue';
import CardActionCard from './cards/CardActionCard.vue';
import TopUpPayInCard from './cards/TopUpPayInCard.vue';
import TopUpRequestCard from './cards/TopUpRequestCard.vue';
import { useCardFormat } from './cards/useCardAction';
import { awaitsClient, eventKey, greets, topUpEventKey } from './chatBlocks';
import { useClientFirstName } from '@/composables/useClientFirstName';

const props = defineProps<{
  block: ChatBlock;
  state?: BlockState;
  /** The task this thread belongs to, as its screen loaded it; null outside a task screen. */
  task?: ConciergeRequestView | null;
  /** A request of this card (or another one) is in flight. */
  busy?: boolean;
  /** Why the server refused this card's last answer. */
  error?: string;
  /** Re-read the feed after a concierge card's call (its new state comes with the page). */
  reread?: () => Promise<void> | void;
}>();
const emit = defineEmits<{ answer: [text: string]; attach: [] }>();
const { t } = useI18n();
const clientName = useClientFirstName();
function eventLine(b: Extract<ChatBlock, { type: 'CONCIERGE_EVENT' }>): string {
  if (!greets(b.event)) return t(eventKey(b));
  const hello = clientName.value
    ? t('vitrine.concierge.event.hello', { name: clientName.value }) : t('vitrine.concierge.event.helloNoName');
  return `${hello}\n${t(eventKey(b))}`;
}

const value = ref('');
const f = useCardFormat();
const topUpState = computed(() => (props.state?.type === 'TOPUP_EVENT' ? props.state : undefined));
/** What the closing card names: the amount credited to the card, in the card's currency. */
const credited = computed(() => {
  const r = topUpState.value?.request;
  if (!r || !(Number(r.creditedAmount) > 0)) return null;
  return { amount: r.creditedAmount, currency: r.creditCurrency ?? r.requestedCreditCurrency ?? r.amountCurrency };
});
const open = computed(() => awaitsClient(props.state));
const disabled = computed(() => !!props.busy);
/** A finished task: cancelled, rejected, or sent with nothing more to happen. */
const taskClosed = computed(() => ['CANCELLED', 'REJECTED', 'COMPLETED'].includes(props.task?.status ?? ''));

function reread(): Promise<void> | void {
  return props.reread?.();
}

function sendInput() {
  const text = value.value.trim();
  if (!text || disabled.value) return;
  emit('answer', text);
}

/** The field empties once the answer is on the server (the card then closes); a refusal keeps what was typed. */
defineExpose({ clear: () => { value.value = ''; } });
</script>

<style scoped lang="scss">
@use './cards/cards.scss';

.cb-event { margin: 0; white-space: pre-wrap; overflow-wrap: anywhere; }
/* A top-up status line between the cards: small, muted, centred — a note, not a bubble. */
.cb-event--line {
  max-width: 300px; padding: 4px 10px; text-align: center;
  font-size: 12px; line-height: 1.45; color: var(--muted);
}
</style>
